import { AfterViewInit, Directive, ElementRef, EventEmitter, inject, Input, NgZone, output, Output } from '@angular/core';
import { NgControl } from '@angular/forms';
import { DatePicker } from 'primeng/datepicker';

@Directive({
  selector: '[appDateInput]',
  standalone: false
})
export class DateInputDirective implements AfterViewInit {
  @Input() baseDateControlName?: string;
  @Output() customBlur = new EventEmitter<void>();
  @Output() invalidInput = new EventEmitter<boolean>();
  isYearMonthInput = output<boolean>();
  private el = inject(ElementRef);
  private zone = inject(NgZone);
  private cachedInput = '';

  private ngControl = inject(NgControl);
  private datepicker = inject(DatePicker);

  ngAfterViewInit(): void {
    // Input-Feld aus p-calendar herausholen
    const input = this.el.nativeElement.querySelector('input');
    const yearMonthExp = /^(\+)?(\d+)\s*([myj])$/i;

    // Hören auf das onSelect-Event des DatePickers.
    // Wenn ein Datum aus dem Kalender gewählt wird, setzen wir den Invalid-Status zurück.
    this.datepicker.onSelect.subscribe((date: Date) => {
      this.zone.run(() => {
        this.invalidInput.emit(false);
        this.cachedInput = this.formatDate(date); // Cache mit dem korrekten Wert aktualisieren
      });
    });

    if (input) {
      // Input-Listener
      input.addEventListener('input', (e: Event) => {
        this.cachedInput = (e.target as HTMLInputElement).value;
      });

      // Blur-Listener
      input.addEventListener('blur', () => {
        // Schutzmechanismus. Wenn der Kalender noch sichtbar ist,
        // handelt es sich um einen internen Blur, den wir ignorieren.
        if (this.datepicker.overlayVisible) {
          return;
        }

        this.zone.run(() => {
          const currentInputValue = this.cachedInput || input.value;
          const yearMonthMatch = yearMonthExp.test(currentInputValue);
          this.isYearMonthInput.emit(yearMonthMatch);

          const parsedDate = this.parseShortcut(currentInputValue);
          if (parsedDate) {
            this.ngControl.control?.setValue(parsedDate, { emitEvent: true });
            input.value = this.formatDate(parsedDate);
            input.dispatchEvent(new Event('input', { bubbles: true }));
          }
          this.customBlur.emit();
        });
      });
    }
  }

  private getBaseDate(): Date {
    if (this.baseDateControlName && this.ngControl.control?.parent) {
      const baseControl = this.ngControl.control.parent.get(this.baseDateControlName);
      if (baseControl?.value) {
        const potentialDate = new Date(baseControl.value);
        if (!isNaN(potentialDate.getTime())) {
          return potentialDate;
        }
      }
    }
    return new Date(); // Fallback auf das aktuelle Datum
  }

  private parseShortcut(input: string): Date | null {
    if (input) {
      const now = this.getBaseDate();
      const calcDateRegExp = /^(\+)?(\d+)\s*([tdwmjy])$/i;
      const dateRegexp = /^(0[1-9]|[12][0-9]|3[01])(0[1-9]|1[0-2])(19[0-9]{2}|[2-9][0-9]{3}|[0-9]{2})$/;
      const dateWithDotsRegexp = /^(0?[1-9]|[12][0-9]|3[01])\.(0?[1-9]|1[0-2])\.(\d{2}|\d{4})$/;

      const calcMatch = input.trim().toLowerCase().match(calcDateRegExp);
      const dateMatch = input.trim().toLowerCase().match(dateRegexp);
      const dateWithDotsMatch = input.trim().toLowerCase().match(dateWithDotsRegexp);

      let parsedDate: Date | null = null;

      if (calcMatch) {
        const value = parseInt(calcMatch[2], 10);
        const unit = calcMatch[3];
        parsedDate = new Date(now);

        switch (unit) {
          case 't':
          case 'd':
            parsedDate.setDate(parsedDate.getDate() + value);
            break;
          case 'w':
            parsedDate.setDate(parsedDate.getDate() + value * 7);
            break;
          case 'm':
            parsedDate = this.addMonthsSafe(parsedDate, value);
            break;
          case 'j':
          case 'y':
            parsedDate = this.addYearsSafe(parsedDate, value);
            break;
        }
      } else if (dateMatch) {
        const day = parseInt(dateMatch[1], 10);
        const month = parseInt(dateMatch[2], 10) - 1;
        const year = parseInt(dateMatch[3], 10);
        const newYearValue = this.toFourDigitYearAlwaysCurrentCentury(year);
        parsedDate = new Date(newYearValue, month, day);
      } else if (dateWithDotsMatch) {
        const day = parseInt(dateWithDotsMatch[1], 10);
        const month = parseInt(dateWithDotsMatch[2], 10) - 1;
        const year = parseInt(dateWithDotsMatch[3], 10);
        const newYearValue = this.toFourDigitYearAlwaysCurrentCentury(year);
        parsedDate = new Date(newYearValue, month, day);
      }

      if (parsedDate && this.isBeforeToday(parsedDate)) {
        //this.invalidInput.emit(true);
        return null;
      }

      if (parsedDate) {
        this.invalidInput.emit(false);
        return parsedDate;
      }

      this.invalidInput.emit(true);
      return null;
    }
    return null;
  }

  private isBeforeToday(date: Date): boolean {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Setze die Uhrzeit auf Mitternacht
    return date < today;
  }

  private formatDate(date: Date): string {
    // Formatierung explizit auf dd.mm.yyyy setzen
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    return `${day}.${month}.${year}`;
  }

  private toFourDigitYearAlwaysCurrentCentury(year: number) {
    const currentYear = new Date().getFullYear();
    const currentCentury = Math.floor(currentYear / 100) * 100;

    if (year < 100) {
      return currentCentury + year;
    }

    return year;
  }

  /**
   * Methode zur Überprüfung und Korrektur, ob bei der Konvertierung von 1M, 2M usw. Überläufe geschehen.
   * @param date Startdatum
   * @param months Monate, die hinzugefügt werden sollen.
   * @private
   */
  private addMonthsSafe(date: Date, months: number) {
    const year = date.getFullYear();
    const month = date.getMonth() + months;
    const day = date.getDate();

    const temp = new Date(year, month, day);

    //Überlauf-Prüfung: Liegt das Ergebnis noch im Zielmonat?
    if (temp.getMonth() !== ((month % 12) + 12) % 12) {
      //Wenn nicht, gib den letzten Tag des Zielmonats zurück
      return new Date(year, month + 1, 0);
    }
    return temp;
  }

  /**
   * Methode zur Überprüfung und Korrektur, ob bei der Konvertierung von 1J, 2J usw. Überläufe geschehen.
   * @param date Startdatum
   * @param years Jahre, die hinzugefügt werden sollen.
   * @private
   */
  private addYearsSafe(date: Date, years: number) {
    const year = date.getFullYear() + years;
    const month = date.getMonth();
    const day = date.getDate();

    const temp = new Date(year, month, day);

    //Überlauf-Prüfung: Liegt das Ergebnis noch im Zielmonat?
    if (temp.getMonth() !== month) {
      //Wenn nicht, gib den letzten Tag des Zielmonats zurück
      return new Date(year, month + 1, 0);
    }
    return temp;
  }
}
